/**
 * i18n — 中英双语字典（雅致短句，忌机翻腔）。
 * t() 每次调用时读取当前语言；UI 在语言切换时整体重渲染。
 */
import type { Language } from '@shared/config/theme';

type Entry = { zh: string; en: string };

export const DICT = {
  title: { zh: '弹珠跳棋', en: 'Marble Checkers' },
  titleLatin: { zh: 'MARBLE CHECKERS', en: 'MARBLE CHECKERS' },
  subtitle: {
    zh: '暖室毡面 · 手工玻璃弹珠 · 六芒星棋盘',
    en: 'Warm parlor felt · hand-blown glass · hexagram board',
  },
  tagline: {
    zh: '一步一跳，把十颗星子送回对岸的家',
    en: 'Step and hop — carry your ten stars home',
  },
  start: { zh: '一键开局', en: 'Quick Start' },
  custom: { zh: '自定义对局', en: 'Custom Game' },
  // v4.0（SCREENS §14.2）：一键开局副行如实播报当前配置的模式与人数。
  qsModeAi: { zh: '人机 · {n}人', en: 'vs AI · {n}P' },
  qsModeHotseat: { zh: '热座 · {n}人', en: 'Hotseat · {n}P' },

  // ── 组队对战（v4.0 · SCREENS §14.1/§15） ──
  teamEntry: { zh: '组队对战', en: 'Team Battle' },
  teamEntrySub: {
    zh: '2v2 · 3v3 · 人+AI 自由搭配',
    en: '2v2 · 3v3 · humans + AI in any mix',
  },
  teamFormat: { zh: '赛制', en: 'Format' },
  teamSideA: { zh: 'A 队 · 金', en: 'Team A · Gold' },
  teamSideB: { zh: 'B 队 · 青', en: 'Team B · Cyan' },
  teamSeatN: { zh: '座位 {n}', en: 'Seat {n}' },
  teamOrderNote: {
    zh: '座位按回合顺序 A/B 交错，队友由座次自动派生',
    en: 'Seats alternate A/B in turn order; teams derive from seating',
  },
  seatMe: { zh: '我', en: 'Me' },
  seatPlayerN: { zh: '玩家{n}', en: 'Player {n}' },
  stratBalanced: { zh: '均衡', en: 'Balanced' },
  stratAggressive: { zh: '进攻', en: 'Aggressive' },
  stratDefensive: { zh: '防守', en: 'Defensive' },
  stratCooperative: { zh: '协作', en: 'Cooperative' },
  stratTipBalanced: { zh: '攻守兼备的默认人格', en: 'The all-round default' },
  stratTipAggressive: { zh: '快速推进，偏爱连跳长链', en: 'Races forward, loves long chains' },
  stratTipDefensive: { zh: '卡位堵桥，延缓对手抵达', en: 'Blocks bridges, slows rivals' },
  stratTipCooperative: { zh: '为队友铺路搭桥', en: 'Builds bridges for teammates' },
  // 局内 HUD 座位牌的紧凑人格标记。
  stratShortBalanced: { zh: '衡', en: 'B' },
  stratShortAggressive: { zh: '攻', en: 'A' },
  stratShortDefensive: { zh: '守', en: 'D' },
  stratShortCooperative: { zh: '协', en: 'C' },
  teamPresets: { zh: '快速预设', en: 'Quick presets' },
  presetMeAi: { zh: '我和AI搭档', en: 'Me + AI partner' },
  presetAllAi: { zh: '全队AI观摩', en: 'All-AI spectate' },
  presetCustom: { zh: '自定义', en: 'Custom' },
  teamStart: { zh: '开始对战', en: 'Start Battle' },
  teamSpectate: { zh: '开始观战', en: 'Spectate' },
  teamWinA: { zh: 'A 队（金）', en: 'Team A (Gold)' },
  teamWinB: { zh: 'B 队（青）', en: 'Team B (Cyan)' },
  settings: { zh: '设置', en: 'Settings' },
  sound: { zh: '声音', en: 'Sound' },
  home: { zh: '主页', en: 'Home' },
  yourTurn: { zh: '你的回合', en: 'Your turn' },
  aiThinking: { zh: '对手思考中…', en: 'Opponent thinking…' },
  seatTurn: { zh: '回合', en: 'Turn' },
  noMoves: { zh: '无子可动，跳过回合', en: 'No legal move — skipped' },
  timeoutAuto: { zh: '超时，自动行棋', en: 'Time out — auto move' },
  selectHint: { zh: '点选你的弹珠，再点落点', en: 'Tap your marble, then a landing' },
  win: { zh: '十子归营！', en: 'All ten home!' },
  winner: { zh: '胜者', en: 'Winner' },
  turns: { zh: '回合数', en: 'Rounds' },
  moves: { zh: '步数', en: 'Moves' },
  again: { zh: '再来一局', en: 'Play Again' },
  backHome: { zh: '回到主页', en: 'Home' },
  theme: { zh: '主题', en: 'Theme' },
  quality: { zh: '画质', en: 'Quality' },
  qualityLow: { zh: '流畅', en: 'Low' },
  qualityMed: { zh: '均衡', en: 'Medium' },
  qualityHigh: { zh: '极致', en: 'High' },
  match: { zh: '对局', en: 'Match' },
  modeAi: { zh: '人机对弈', en: 'vs AI' },
  modeHotseat: { zh: '同屏对弈', en: 'Hotseat' },
  players: { zh: '人数', en: 'Players' },
  difficulty: { zh: 'AI 棋力', en: 'AI Level' },
  easy: { zh: '闲手', en: 'Casual' },
  medium: { zh: '棋手', en: 'Club' },
  hard: { zh: '棋士', en: 'Master' },
  audio: { zh: '声音', en: 'Audio' },
  sfx: { zh: '音效', en: 'SFX' },
  ambient: { zh: '环境声', en: 'Ambience' },
  mute: { zh: '静音', en: 'Mute' },
  interface: { zh: '界面', en: 'Interface' },
  language: { zh: '语言', en: 'Language' },
  hints: { zh: '显示提示环', en: 'Show hints' },
  iridescence: { zh: '弹珠虹彩', en: 'Iridescence' },
  speed: { zh: '动画速度', en: 'Animation' },
  timer: { zh: '回合计时', en: 'Turn timer' },
  off: { zh: '关', en: 'Off' },
  template: { zh: '配置模板', en: 'Preset Template' },
  exportTpl: { zh: '导出当前配置', en: 'Export current' },
  importTpl: { zh: '导入配置 JSON', en: 'Import JSON' },
  apply: { zh: '应用', en: 'Apply' },
  done: { zh: '完成', en: 'Done' },
  tplOk: { zh: '模板已应用', en: 'Template applied' },
  tplBad: { zh: 'JSON 无法解析', en: 'Invalid JSON' },
  secUnit: { zh: '秒', en: 's' },
  you: { zh: '你', en: 'You' },
  ai: { zh: 'AI', en: 'AI' },
  loading: { zh: '正在织一张毡子…', en: 'Weaving the felt…' },
  defeat: { zh: '棋差一着', en: 'So Close' },
  winnerIs: { zh: '夺得十子归营', en: 'brought all ten home' },

  // ── 网络模式（联机大厅 / 匹配 / 对局） ──
  netTitle: { zh: '联机对局', en: 'Online Play' },
  netMatch: { zh: '随机匹配', en: 'Random Match' },
  netMatchDesc: {
    zh: '与线上棋手对弈，开局即战',
    en: 'Play against online rivals, jump straight in',
  },
  netMatching: { zh: '正在匹配对手…', en: 'Pairing you with rivals…' },
  netConnecting: { zh: '正在连接服务器…', en: 'Connecting…' },
  netRetry: { zh: '重试连接', en: 'Retry' },
  netClose: { zh: '关闭', en: 'Close' },
  netNickname: { zh: '昵称', en: 'Nickname' },
  netNicknamePh: { zh: '输入昵称（12 字内）', en: 'Nickname (max 12 chars)' },
  netMode: { zh: '人数模式', en: 'Mode' },
  netQuickMatch: { zh: '开始匹配', en: 'Find Match' },
  netParty: { zh: '派对组队', en: 'Party Up' },
  netPartyCreate: { zh: '创建队伍', en: 'Create Party' },
  netPartyJoin: { zh: '加入队伍', en: 'Join Party' },
  netPartyCodePh: { zh: '3 位邀请码', en: '3-char code' },
  netPartyShare: {
    zh: '分享邀请码，好友输入即可加入',
    en: 'Share the code — friends type it to join',
  },
  netPartyLeave: { zh: '离开队伍', en: 'Leave Party' },
  netPartyLeader: { zh: '队长', en: 'Leader' },
  netPartyWaitLeader: { zh: '等待队长开始匹配…', en: 'Waiting for the leader to queue…' },
  netCancelMatch: { zh: '取消匹配', en: 'Cancel' },
  netQueuedFor: { zh: '已等待', en: 'Waiting' },
  netSoftTip: { zh: '正在努力寻找对手，请稍候…', en: 'Still searching, hang tight…' },
  netSlowTip: { zh: '当前模式棋手较少，可尝试其他人数', en: 'Few players here — try another mode' },
  netQueueDeficit: { zh: '还差 {n} 名对手', en: 'Waiting for {n} more' },
  netQueueFillAi: { zh: '{s} 秒后将由 AI 补位开局', en: 'AI fills empty seats in {s}s' },
  netMatched: { zh: '匹配成功！', en: 'Match found!' },
  netReturnMatch: { zh: '返回对局', en: 'Rejoin Match' },
  netInMatchTip: { zh: '你有一场进行中的对局', en: 'You have a match in progress' },
  netCopied: { zh: '已复制邀请码', en: 'Code copied' },
  netRtt: { zh: '延迟', en: 'Ping' },
  netAutoMove: { zh: '超时，系统托管行棋', en: 'Timed out — autopilot moved' },
  netPeerAuto: { zh: '{name} 超时，系统托管', en: '{name} timed out — autopilot' },
  netPeerOffline: { zh: '{name} 已断线，等待重连', en: '{name} disconnected, awaiting return' },
  netPeerBack: { zh: '{name} 已重连', en: '{name} reconnected' },
  netPeerResigned: { zh: '{name} 已认输', en: '{name} resigned' },
  netNoMovesName: { zh: '{name} 无子可动，跳过回合', en: '{name} has no move — skipped' },
  netTurnWarning: { zh: '你的回合即将超时', en: 'Your turn is about to time out' },
  netRematchWait: { zh: '等待其他玩家…', en: 'Waiting for others…' },
  netRematchVotes: { zh: '再来一局 {v}/{n}', en: 'Rematch {v}/{n}' },
  netRoomClosed: { zh: '房间已关闭', en: 'Room closed' },
  netRematchFailed: { zh: '再来一局未达成', en: 'Rematch fell through' },
  netAborted: { zh: '对局已终止', en: 'Match aborted' },
  netErrNoMatch: { zh: '当前没有进行中的对局', en: 'No match in progress' },
  netMoveRejected: { zh: '走法未被接受', en: 'Move rejected' },
  netNotYourTurn: { zh: '还没到你的回合', en: 'Not your turn yet' },
  netStandings: { zh: '最终排名', en: 'Final standings' },
  netResignedTag: { zh: '已认输', en: 'Resigned' },
  netErrPartyNotFound: { zh: '邀请码不存在或已失效', en: 'Code not found or expired' },
  netErrPartyFull: { zh: '队伍已满', en: 'Party is full' },
  netErrPartyTooLarge: { zh: '队伍人数超过该模式上限', en: 'Party too large for this mode' },
  netErrNotLeader: { zh: '只有队长可以开始匹配', en: 'Only the leader can queue' },
  netErrAlreadyParty: { zh: '请先离开当前队伍', en: 'Leave your current party first' },
  netErrQueueFull: { zh: '匹配队列繁忙，请稍后再试', en: 'Queue is busy — try again soon' },
  netErrVersion: { zh: '版本过旧，请刷新页面', en: 'Client outdated — please refresh' },
  netErrBusy: { zh: '服务器繁忙，请稍后再试', en: 'Server busy — try again soon' },
  netErrRateLimited: { zh: '操作太频繁，请慢一点', en: 'Too fast — slow down a little' },
  netErrGeneric: { zh: '联机服务异常，请稍后再试', en: 'Online service error — retry later' },

  // ── Combo 计分（v2.1） ──
  comboTitle: { zh: '连跳战绩', en: 'Combo highlights' },
  comboRow: {
    zh: '最高 ×{max} · {hops} 跳 · {chains} 连击 · {pts} 分',
    en: 'max ×{max} · {hops} hops · {chains} chains · {pts} pts',
  },
  comboBest: { zh: '最佳连跳', en: 'BEST COMBO' },
  comboBadge: { zh: 'COMBO ×{n}', en: 'COMBO ×{n}' },

  // ── Tutorial 新手引导（v2.1 · SCREENS §12） ──
  tutStep: { zh: '{a} / {b}', en: '{a} / {b}' },
  tutSkip: { zh: '跳过', en: 'Skip' },
  tutNext: { zh: '下一步', en: 'Next' },
  tutFinish: { zh: '完成', en: 'Done' },
  tutCampT: { zh: '你的阵营', en: 'Your camp' },
  tutCampB: {
    zh: '这是你的阵营，目标是把 10 颗弹珠全部搬进对角',
    en: 'Your camp — race all 10 marbles to the opposite triangle',
  },
  tutSelectT: { zh: '点选弹珠', en: 'Pick a marble' },
  tutSelectB: { zh: '点选一颗你的弹珠', en: 'Tap one of your marbles' },
  tutDestT: { zh: '落点', en: 'Destinations' },
  tutDestB: {
    zh: '绿圈是落点：相邻为走步，跳格为跳跃；发光远点 = 连跳可达',
    en: 'Green rings are destinations — adjacent = step, beyond a marble = hop; glowing far holes are chain-hop landings',
  },
  tutChainT: { zh: '连跳', en: 'Chain hops' },
  tutChainB: {
    zh: '一次可以连跳多格，选好终点一次走完',
    en: 'A chain can span several hops — pick the far landing to fly the whole path',
  },
  tutTimerT: { zh: '回合计时', en: 'Turn timer' },
  tutTimerB: {
    zh: '轮到你时计时开始，超时将由系统托管行棋',
    en: 'Your turn is timed; timeouts are auto-played',
  },
  tutComboT: { zh: 'COMBO 表现分', en: 'COMBO score' },
  tutComboB: {
    zh: '连跳会计入 COMBO 表现分，结算见高低！',
    en: 'Chains build your COMBO score — compare at results!',
  },
  tutReplay: { zh: '重看教程', en: 'Replay tutorial' },
  tutReplayOk: {
    zh: '已重置，下次开局将播放教程',
    en: 'Reset — the tutorial plays on your next match',
  },

  // ── 分享（v2.1 · SCREENS §13） ──
  share: { zh: '分享', en: 'Share' },
  shareTitle: { zh: '分享到', en: 'Share to' },
  shareCopy: { zh: '复制链接', en: 'Copy link' },
  shareCopied: { zh: '已复制，去粘贴分享吧', en: 'Copied — paste to share' },
  shareWeibo: { zh: '微博', en: 'Weibo' },
  shareQQ: { zh: 'QQ', en: 'QQ' },
  shareX: { zh: 'X', en: 'X' },
  shareFacebook: { zh: 'Facebook', en: 'Facebook' },
  shareWhatsApp: { zh: 'WhatsApp', en: 'WhatsApp' },
  shareTelegram: { zh: 'Telegram', en: 'Telegram' },
  sharePlainText: {
    zh: '一起来玩《弹珠跳棋》！暖室毡面上的六芒星棋局：',
    en: 'Come play Marble Checkers — a warm-parlor hexagram board game:',
  },
  shareInviteText: {
    zh: '一起来玩《弹珠跳棋》！输入组队码 {code} 与我同队：',
    en: 'Join my Marble Checkers party with code {code}:',
  },
  shareResultText: {
    zh: '《弹珠跳棋》{n}人局落幕，{name} 夺冠！我的最大连跳 ×{max}，表现分 {pts}。来挑战我：',
    en: '{name} won a {n}-player Marble Checkers match! My best chain ×{max}, {pts} pts. Beat me:',
  },

  // ── 调试面板 ──
  dbgPause: { zh: '暂停', en: 'Pause' },
  dbgResume: { zh: '继续', en: 'Resume' },
  dbgClear: { zh: '清空', en: 'Clear' },
  dbgCopy: { zh: '复制', en: 'Copy' },
  dbgLayout: { zh: '棋盘布局', en: 'Board Layout' },
  dbgClose: { zh: '关闭', en: 'Close' },
  dbgNoScene: { zh: '（当前无对局场景）', en: '(no active match scene)' },
} as const satisfies Record<string, Entry>;

export type DictKey = keyof typeof DICT;

export function tr(key: DictKey, lang: Language): string {
  const e = DICT[key];
  return e[lang];
}

/** 带参翻译：替换 `{name}` 形式的占位符。 */
export function trf(key: DictKey, lang: Language, vars: Record<string, string | number>): string {
  let s = tr(key, lang);
  for (const [k, v] of Object.entries(vars)) {
    s = s.replaceAll(`{${k}}`, String(v));
  }
  return s;
}

/** 服务端 ERROR code → 文案键（未覆盖的落入 netErrGeneric）。 */
export function netErrorKey(code: string): DictKey {
  switch (code) {
    case 'PARTY_NOT_FOUND':
      return 'netErrPartyNotFound';
    case 'PARTY_FULL':
      return 'netErrPartyFull';
    case 'PARTY_TOO_LARGE':
      return 'netErrPartyTooLarge';
    case 'NOT_PARTY_LEADER':
      return 'netErrNotLeader';
    case 'ALREADY_IN_PARTY':
      return 'netErrAlreadyParty';
    case 'QUEUE_FULL':
      return 'netErrQueueFull';
    case 'VERSION_MISMATCH':
      return 'netErrVersion';
    case 'SERVER_BUSY':
      return 'netErrBusy';
    case 'RATE_LIMITED':
      return 'netErrRateLimited';
    default:
      return 'netErrGeneric';
  }
}
